import { InjectionToken, inject } from '@angular/core';
import { EffyEnvironment, EFFY_ENVIRONMENT } from '@effy-tech/angular-common/environment';
import { HttpContextToken, HttpContext } from '@angular/common/http';

// WS Config for Effy usage
const EFFY_WS_CONFIG = {
    users: {
        [EffyEnvironment.Dev]: 'https://users.dev.api.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://users.rct.api.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://users.api.effy.fr',
    },
    address: {
        [EffyEnvironment.Dev]: 'https://address.dev.api.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://address.api.effy.fr',
        [EffyEnvironment.Prod]: 'https://address.api.effy.fr',
    },
    primeo: {
        [EffyEnvironment.Dev]: 'https://dev.primeo.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://rct.primeo.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://primeo.quelleenergie.fr',
    },
    sherlock: {
        [EffyEnvironment.Dev]: 'https://dev.sherlock.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://rct.sherlock.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://sherlock.effy-connect.fr',
    },
    ecGateway: {
        [EffyEnvironment.Dev]: 'https://ec-gateway.api.dev.effyconnect.net',
        [EffyEnvironment.Rct]: 'https://ec-gateway.api.rct.effyconnect.net',
        [EffyEnvironment.Prod]: 'https://ec-gateway.api.effy-connect.fr',
    },
    ecGatewayNew: {
        [EffyEnvironment.Dev]: 'https://ec.gateway.dev.effyconnect.net',
        [EffyEnvironment.Rct]: 'https://ec.gateway.rct.effyconnect.net',
        [EffyEnvironment.Prod]: 'https://ec.gateway.effy-connect.fr',
    },
    salesforceProLead: {
        [EffyEnvironment.Dev]: 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
        [EffyEnvironment.Rct]: 'https://test.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
        [EffyEnvironment.Prod]: 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
    },
    effy: {
        [EffyEnvironment.Dev]: 'https://dev.effy.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://rct.effy.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://www.effy.fr',
    },
    commonAssets: {
        [EffyEnvironment.Dev]: 'https://dev.effy.quelleenergie.net/common-assets/assets-2024/',
        [EffyEnvironment.Rct]: 'https://rct.effy.quelleenergie.net/common-assets/assets-2024/',
        [EffyEnvironment.Prod]: 'https://www.effy.fr/common-assets/assets-2024/',
    },
    trackingPro: {
        [EffyEnvironment.Dev]: 'https://europe-west1-dev-platform-datas-43d1566e.cloudfunctions.net/dev-platform-datas-webhook-pro-trac',
        [EffyEnvironment.Rct]: 'https://europe-west1-rct-platform-datas-bf9f2524.cloudfunctions.net/rct-platform-datas-webhook-pro-trac',
        [EffyEnvironment.Prod]: 'https://europe-west1-prod-platform-datas-bb779263.cloudfunctions.net/prod-platform-datas-webhook-pro-trac',
    },
    trackingAPIKey: {
        [EffyEnvironment.Dev]: ']c28wI0jH_*z0$2ZGH0',
        [EffyEnvironment.Rct]: '6M|)VDN[Y.#tO6A$[s#Ikp(?',
        [EffyEnvironment.Prod]: '$&3c45Xr_#}Kh62Ubk56',
    },
    sogefi: {
        [EffyEnvironment.Dev]: 'https://tiles.sogefi-sig.com/2Bh3jpjKhiuLxfcmcxTikya14WadbRjF5Sm',
        [EffyEnvironment.Rct]: 'https://tiles.sogefi-sig.com/2Bh3jpjKhiuLxfcmcxTikya14WadbRjF5Sm',
        [EffyEnvironment.Prod]: 'https://tiles.sogefi.io',
    },
    userEnrichment: {
        [EffyEnvironment.Dev]: 'https://user-enrichment.dev.api.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://user-enrichment.rct.api.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://user-enrichment.api.effy.fr',
    },
    notification: {
        [EffyEnvironment.Dev]: 'https://notification.dev.api.quelleenergie.net',
        [EffyEnvironment.Rct]: 'https://notification.rct.api.quelleenergie.net',
        [EffyEnvironment.Prod]: 'https://notification.api.effy.fr',
    },
};
// Token to inject
const TOKEN_EFFY_WS_CONFIG = new InjectionToken('effy-ws.config');
// Token to inject only
const SHERLOCK_AUTH_LEGACY = new InjectionToken('SherlockAuthLegacy');

const ADDRESS_WEB_SERVICE = new InjectionToken('Address Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.address[inject(EFFY_ENVIRONMENT)]}`;
        return {
            base: baseApi,
            search: `${baseApi}/search`,
        };
    },
});

const COST_ESTIMATOR_WEB_SERVICE = new InjectionToken('Cost Estimator Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.ecGatewayNew[inject(EFFY_ENVIRONMENT)]}`;
        return {
            base: baseApi,
            estimate: `${baseApi}/api/costestimator/v3/estimate`,
        };
    },
});

const EC_GATEWAY_WEB_SERVICE = new InjectionToken('EC Gateway Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.ecGatewayNew[inject(EFFY_ENVIRONMENT)]}`;
        const compose = `${baseApi}/api/compose`;
        const primeo = `${baseApi}/api/primeo`;
        const worksV2 = `${compose}/v2/works`;
        const transaction = `${compose}/v1/payments/bonus`;
        const users = `${baseApi}/api/users`;
        return {
            base: baseApi,
            compose,
            accommodations: `${compose}/v1/accommodations`,
            me: `${compose}/v1/me`,
            customerDocument: `${compose}/v1/me/document`,
            customerDocuments: `${compose}/v1/me/documents`,
            requestValidation: `${compose}/v1/me/request-validation`,
            contactUs: `${compose}/v1/salesforce/contact-us`,
            documentsMerge: `${compose}/v1/documents/merge`,
            projects: `${compose}/v1/projects`,
            projectsV2: `${compose}/v2/projects`, // For now, this is only used for global renovation projects
            callState: `${compose}/v1/qualification/call-state`,
            worksV1: `${compose}/v1/works`,
            worksV2,
            newWorks: `${worksV2}/new`,
            engagedWorks: `${worksV2}/engaged`,
            engagedWorksWithId: `${worksV2}/:workId`,
            bonuses: `${compose}/v1/bonuses`,
            artisans: `${baseApi}/api/annuaire`,
            pistes: `${baseApi}/api/sherlock/v1/pistes`,
            intentionnalite: `${baseApi}/api/sherlock/v1/pistes/intentionnalite`,
            paymentAccountingDocuments: `${baseApi}/api/payment/v1/accounting-documents`,
            paymentTransactions: `${baseApi}/api/payment/v1/transactions`,
            transaction,
            payout: `${transaction}/payout`,
            taxSituations: `${compose}/v1/tax-situations`,
            documents: `${compose}/v1/documents`,
            paymentsBonus: `${compose}/v1/payments/bonus-payout`,
            invoices: `${compose}/v1/invoices`,
            primeoDocuments: `${primeo}/documents/v3`,
            primeoProjects: `${primeo}/projects/v3`,
            primeoBonus: `${primeo}/projects/v4`,
            appointments: `${compose}/v1/appointments`,
            appointmentsV2: `${compose}/v2/appointments`,
            userEnrichment: `${baseApi}/api/userenrichment/v1/enrichment`,
            relationships: `${compose}/v1/relationships`,
            user: `${users}/v1/users`,
            login: `${users}/v1/users/login`,
            autoLogin: `${users}/v1/users/autologin`,
            passwordsForgot: `${users}/v1/users/passwords/forgot`,
            passwordsReset: `${users}/v1/users/passwords/reset`,
            passwordsLoggedReset: `${users}/v1/users/passwords/logged_reset`,
            token: `${users}/v1/users/token`,
            passwordless: `${users}/v1/passwordless_users`,
            autolink: `${users}/v1/users/autolink`,
            solarCalculator: `${baseApi}/api/solarcalculator/v1`,
        };
    },
});

const CONTEXT_TOKEN_SESSION_TOKEN = new HttpContextToken(() => undefined);
const CONTEXT_SESSION_TOKEN_TRUE = new HttpContext().set(CONTEXT_TOKEN_SESSION_TOKEN, true);
const CONTEXT_SESSION_TOKEN_FALSE = new HttpContext().set(CONTEXT_TOKEN_SESSION_TOKEN, false);

const NOTIFICATION_WEB_SERVICE = new InjectionToken('Notification Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.notification[inject(EFFY_ENVIRONMENT)]}`;
        return {
            base: baseApi,
            send: `${baseApi}/v1/mailing/send`,
        };
    },
});

const PRECARITY_LEVEL_WEB_SERVICE = new InjectionToken('Precarity Level Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.ecGatewayNew[inject(EFFY_ENVIRONMENT)]}`;
        return {
            taxRevenue: `${baseApi}/api/precaritylevel/v2/tax_revenue`,
        };
    },
});

const PRIMEO_WEB_SERVICE = new InjectionToken('Primeo Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.primeo[inject(EFFY_ENVIRONMENT)]}`;
        return {
            base: baseApi,
        };
    },
});

const SHERLOCK_WEB_SERVICE = new InjectionToken('Sherlock Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.sherlock[inject(EFFY_ENVIRONMENT)]}/api/v1`;
        return {
            base: baseApi,
        };
    },
});

const SOGEFI_WEB_SERVICE = new InjectionToken('Sogefi Web Service', {
    providedIn: 'root',
    factory: () => {
        const baseApi = `${EFFY_WS_CONFIG.sogefi[inject(EFFY_ENVIRONMENT)]}`;
        return {
            base: baseApi,
            tileset: `${baseApi}/pci/v2/tiles/tileset/`,
        };
    },
});

const EFFY_WS_CONFIG_MOCK = {
    users: {
        [EffyEnvironment.Dev]: 'https://todo/users/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/users/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/users/fake-url/prod',
    },
    address: {
        [EffyEnvironment.Dev]: 'https://todo/address/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/address/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/address/fake-url/prod',
    },
    primeo: {
        [EffyEnvironment.Dev]: 'https://todo/primeo/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/primeo/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/primeo/fake-url/prod',
    },
    sherlock: {
        [EffyEnvironment.Dev]: 'https://todo/sherlock/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/sherlock/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/sherlock/fake-url/prod',
    },
    ecGateway: {
        [EffyEnvironment.Dev]: 'https://todo/ec-gateway/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/ec-gateway/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/ec-gateway/fake-url/prod',
    },
    ecGatewayNew: {
        [EffyEnvironment.Dev]: 'https://todo/ec-gateway-new/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/ec-gateway-new/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/ec-gateway-new/fake-url/prod',
    },
    salesforceProLead: {
        [EffyEnvironment.Dev]: 'https://todo/salesforce-pro-lead/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/salesforce-pro-lead/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/salesforce-pro-lead/fake-url/prod',
    },
    effy: {
        [EffyEnvironment.Dev]: 'https://todo/effy/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/effy/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/effy/fake-url/prod',
    },
    commonAssets: {
        [EffyEnvironment.Dev]: 'https://todo/common-assets/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/common-assets/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/common-assets/fake-url/prod',
    },
    trackingPro: {
        [EffyEnvironment.Dev]: 'https://todo/tracking-pro/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/tracking-pro/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/tracking-pro/fake-url/prod',
    },
    trackingAPIKey: {
        [EffyEnvironment.Dev]: 'https://todo/tracking-pro/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/tracking-pro/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/tracking-pro/fake-url/prod',
    },
    sogefi: {
        [EffyEnvironment.Dev]: 'https://todo/tracking-pro/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/tracking-pro/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/tracking-pro/fake-url/prod',
    },
    userEnrichment: {
        [EffyEnvironment.Dev]: 'https://todo/user-enrichment/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/user-enrichment/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/user-enrichment/fake-url/prod',
    },
    notification: {
        [EffyEnvironment.Dev]: 'https://todo/notification/fake-url/dev',
        [EffyEnvironment.Rct]: 'https://todo/notification/fake-url/rct',
        [EffyEnvironment.Prod]: 'https://todo/notification/fake-url/prod',
    },
};

/**
 * Generated bundle index. Do not edit.
 */

export { ADDRESS_WEB_SERVICE, CONTEXT_SESSION_TOKEN_FALSE, CONTEXT_SESSION_TOKEN_TRUE, CONTEXT_TOKEN_SESSION_TOKEN, COST_ESTIMATOR_WEB_SERVICE, EC_GATEWAY_WEB_SERVICE, EFFY_WS_CONFIG, EFFY_WS_CONFIG_MOCK, NOTIFICATION_WEB_SERVICE, PRECARITY_LEVEL_WEB_SERVICE, PRIMEO_WEB_SERVICE, SHERLOCK_AUTH_LEGACY, SHERLOCK_WEB_SERVICE, SOGEFI_WEB_SERVICE, TOKEN_EFFY_WS_CONFIG };
